import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/api/errors';
import { FamilyRecordErrorState } from '@/features/family-record/components/FamilyRecordErrorState';
import { FamilyRecordPendingState } from '@/features/family-record/components/FamilyRecordPendingState';
import { FamilyRecordView } from '@/features/family-record/components/FamilyRecordView';
import { familyQueries } from '@/features/family-record/queries';

import { NotFoundPage } from './NotFoundPage';

/**
 * Route shell for `/families/:guid`. Resolves the param, runs the family
 * detail query, and branches to one of four states. Layout, hash sync, and
 * section composition live in `FamilyRecordView` and its siblings — same
 * shape as `PatientPage`.
 */
export function FamilyPage(): ReactElement {
  const { guid } = useParams<{ guid: string }>();
  const safeGuid = guid ?? '';
  const query = useQuery(familyQueries.detail(safeGuid));

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.code === 'not_found') {
      return <NotFoundPage />;
    }
    return (
      <FamilyRecordErrorState
        error={query.error}
        onRetry={() => {
          query.refetch().catch(() => undefined);
        }}
      />
    );
  }

  if (query.isPending) {
    return <FamilyRecordPendingState guid={safeGuid} />;
  }

  return <FamilyRecordView family={query.data} />;
}
